import React, { useState, useEffect, useRef, useCallback } from 'react';

// Cache for storing previously generated audio
const audioCache = new Map();

// ElevenLabs voice options
const ELEVEN_LABS_VOICES = {
  rachel: { name: "Rachel", voice_id: "21m00Tcm4TlvDq8ikWAM" },
  domi: { name: "Domi", voice_id: "AZnzlk1XvdvUeBnXmlld" },
  bella: { name: "Bella", voice_id: "EXAVITQu4vr4xnSDxMaL" },
  antoni: { name: "Antoni", voice_id: "ErXwobaYiN019PkySvjV" },
  elli: { name: "Elli", voice_id: "MF3mGyEYCl7XYWbV9V6O" },
  josh: { name: "Josh", voice_id: "TxGEqnHWrfWFTfGW9XjX" },
  arnold: { name: "Arnold", voice_id: "VR6AewLTigWG4xSOukaG" },
  adam: { name: "Adam", voice_id: "pNInz6obpgDQGcFmaJgB" },
  sam: { name: "Sam", voice_id: "yoZ06aMxZJJ28mfd3POQ" },
};

// Default voice
const DEFAULT_VOICE = ELEVEN_LABS_VOICES.josh;

const TextToSpeech = ({ text, isDarkMode, language = 'en' }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(DEFAULT_VOICE);
  
  // For streaming audio
  const audioContext = useRef(null);
  const audioQueue = useRef([]);
  const audioSourceNode = useRef(null);
  const abortControllerRef = useRef(null);
  const processingPromise = useRef(null);

  // Clean up function for when component unmounts
  useEffect(() => {
    return () => {
      stopAudio();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, []);

  // Initialize audio context on first interaction
  const initAudioContext = () => {
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContext.current;
  };

  // Clean text for speech by removing code markers and symbols
  const cleanTextForSpeech = useCallback((text) => {
    if (!text) return '';
    
    return text
      // Remove code block markers
      .replace(/```[\s\S]*?```/g, 'code block')
      
      // Remove inline code markers but keep the content
      .replace(/`([^`]+)`/g, '$1')
      
      // Replace hashtags with the word without the hash symbol
      .replace(/#(\w+)/g, '$1')
      
      // Replace multiple consecutive hashtags
      .replace(/#{2,}/g, '')
      
      // Remove HTML/XML tags
      .replace(/<[^>]*>/g, '')
      
      // Replace multiple whitespace with a single space
      .replace(/\s+/g, ' ')
      
      // Remove other code-specific symbols
      .replace(/[{}[\]|\\]/g, ' ')
      
      // Remove URL components that would be read out loud
      .replace(/(https?:\/\/|www\.)/g, '')
      .replace(/\.(com|org|net|io|dev)/g, ' dot com')
      
      .trim();
  }, []);

  // Create very small segments for faster streaming (50-100 chars)
  const createStreamableSegments = useCallback((text) => {
    if (!text) return [];
    
    // First break by sentences
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    
    // Then further break long sentences into smaller chunks for faster streaming
    const streamableChunks = [];
    const maxChunkLength = 100; // Smaller chunks for faster initial playback
    
    sentences.forEach(sentence => {
      if (sentence.length <= maxChunkLength) {
        streamableChunks.push(sentence.trim());
      } else {
        // Split by commas, semicolons, or other natural pauses
        const subParts = sentence.split(/([,;:])/).filter(Boolean);
        let currentChunk = '';
        
        subParts.forEach(part => {
          if (currentChunk.length + part.length > maxChunkLength) {
            if (currentChunk) streamableChunks.push(currentChunk.trim());
            currentChunk = part;
          } else {
            currentChunk += part;
          }
        });
        
        if (currentChunk) streamableChunks.push(currentChunk.trim());
      }
    });
    
    // Ensure first chunk is very small for immediate response
    if (streamableChunks.length > 1 && streamableChunks[0].length > 50) {
      const firstChunk = streamableChunks[0].substring(0, 50);
      const remainder = streamableChunks[0].substring(50);
      streamableChunks[0] = firstChunk;
      streamableChunks.splice(1, 0, remainder);
    }
    
    return streamableChunks;
  }, []);

  // Use streaming API for faster response
  const streamTextToSpeech = async (textSegment) => {
    const cacheKey = `${selectedVoice.voice_id}:${textSegment}`;
    
    if (audioCache.has(cacheKey)) {
      return audioCache.get(cacheKey);
    }

    const apiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;
    if (!apiKey) {
      throw new Error('ElevenLabs API key is not configured');
    }

    // Use streaming API endpoint for faster response
    const url = `https://api.elevenlabs.io/v1/text-to-speech/${selectedVoice.voice_id}/stream`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'xi-api-key': apiKey,
      },
      body: JSON.stringify({
        text: textSegment,
        model_id: 'eleven_turbo_v2',
        voice_settings: {
          stability: 0.3, // Lower stability for faster response
          similarity_boost: 0.5, // Lower similarity for faster response
        },
        output_format: 'mp3_44100_128',
        optimize_streaming_latency: 4, // Maximum optimization for streaming
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(`ElevenLabs API error: ${error.detail || response.statusText}`);
    }

    // Get the streaming response as a readable stream
    const reader = response.body.getReader();
    const chunks = [];
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }

    const blob = new Blob(chunks, { type: 'audio/mpeg' });
    const audioUrl = URL.createObjectURL(blob);
    
    audioCache.set(cacheKey, audioUrl);
    return audioUrl;
  };

  // Load and play audio immediately
  const loadAndPlayAudio = async (audioUrl) => {
    try {
      // console.log('Loading audio URL:', audioUrl);
      // console.log('Current queue length:', audioQueue.current.length);
      
      const context = initAudioContext();
      
      // Resume audio context if it's suspended (autoplay policy)
      if (context.state === 'suspended') {
        // console.log('Audio context suspended, attempting to resume');
        await context.resume();
        // console.log('Audio context resumed:', context.state);
      }
      
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode the audio data
      const audioBuffer = await context.decodeAudioData(arrayBuffer);
      // console.log('Audio buffer decoded, duration:', audioBuffer.duration, 'seconds');
      
      // Create a source node
      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);
      
      // Store reference to currently playing source
      audioSourceNode.current = source;
      
      // Set up event handlers
      source.onended = () => {
        // console.log('Audio segment ended, remaining in queue:', audioQueue.current.length);
        // Play next segment if available
        const nextAudioUrl = audioQueue.current.shift();
        if (nextAudioUrl) {
          // console.log('Playing next segment from queue');
          loadAndPlayAudio(nextAudioUrl);
        } else {
          // console.log('Queue empty, playback complete');
          setIsSpeaking(false);
          setProgress(100);
          setTimeout(() => setProgress(0), 500);
        }
      };
      
      // Start playing
      source.start(0);
      setIsSpeaking(true);
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
      
      // Try to recover by playing the next segment if available
      const nextAudioUrl = audioQueue.current.shift();
      if (nextAudioUrl) {
        // console.log('Attempting to recover by playing next segment');
        setTimeout(() => loadAndPlayAudio(nextAudioUrl), 500);
      }
    }
  };

  // Stop currently playing audio
  const stopAudio = () => {
    if (audioSourceNode.current) {
      try {
        audioSourceNode.current.stop();
      } catch (e) {
        // Ignore errors when stopping audio that's already stopped
      }
      audioSourceNode.current = null;
    }
    
    audioQueue.current = [];
    setIsSpeaking(false);
  };

  // Process text in parallel for better performance
  const processInParallel = async (segments) => {
    // Reset the queue before starting
    audioQueue.current = [];
    // console.log('Starting speech processing for', segments.length, 'segments');
    
    // Process first segment immediately for fast response
    if (segments.length > 0) {
      try {
        // console.log('Processing first segment:', segments[0].substring(0, 20) + '...');
        const firstUrl = await streamTextToSpeech(segments[0]);
        
        // Process remaining segments in parallel with a limit of 3 concurrent requests
        if (segments.length > 1) {
          const remaining = segments.slice(1);
          // console.log('Processing remaining', remaining.length, 'segments in background');
          
          // Start processing the remaining segments immediately but don't wait for them
          (async () => {
            for (let i = 0; i < remaining.length; i += 3) {
              const batch = remaining.slice(i, i + 3);
              // console.log('Processing batch of', batch.length, 'segments');
              
              try {
                const batchPromises = batch.map(segment => streamTextToSpeech(segment));
                const results = await Promise.all(batchPromises);
                
                // Queue these for playback
                audioQueue.current.push(...results);
                // console.log('Added', results.length, 'segments to queue. Queue size:', audioQueue.current.length);
                
                // Update progress
                setProgress(Math.min(90, Math.round(((i + batch.length) / remaining.length) * 90)));
              } catch (error) {
                console.error('Error processing batch:', error);
              }
              
              // Check if aborted
              if (abortControllerRef.current?.signal?.aborted) {
                // console.log('Processing aborted');
                break;
              }
            }
          })();
        }
        
        // Start playback of the first segment immediately
        // console.log('Starting playback of first segment');
        loadAndPlayAudio(firstUrl);
      } catch (error) {
        console.error('Error processing first segment:', error);
        setIsLoading(false);
      }
    }
  };

  // Main function to generate and play speech
  const generateSpeech = async () => {
    // If already speaking, stop
    if (isSpeaking) {
      stopAudio();
      if (processingPromise.current) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        processingPromise.current = null;
      }
      return;
    }
    
    // Initialize
    setIsLoading(true);
    abortControllerRef.current = new AbortController();
    
    try {
      // Clean the text before processing to remove code markers and symbols
      const cleanedText = cleanTextForSpeech(text);
      const segments = createStreamableSegments(cleanedText);
      
      if (segments.length === 0) {
        setIsLoading(false);
        return;
      }
      
      // Start processing and store the promise
      processingPromise.current = processInParallel(segments);
      await processingPromise.current;
    } catch (error) {
      console.error('Error in generateSpeech:', error);
      stopAudio();
    } finally {
      processingPromise.current = null;
      setIsLoading(false);
    }
  };

  // Select appropriate voice based on language
  useEffect(() => {
    if (language === 'ar') {
      setSelectedVoice(ELEVEN_LABS_VOICES.bella);
    } else if (language === 'sr') {
      setSelectedVoice(ELEVEN_LABS_VOICES.antoni);
    } else if (language === 'ru') {
      setSelectedVoice(ELEVEN_LABS_VOICES.elli);
    } else {
      setSelectedVoice(DEFAULT_VOICE);
    }
  }, [language]);

  return (
    <button
      onClick={generateSpeech}
      className={`text-sm p-2 rounded-full ${
        isSpeaking 
          ? isDarkMode 
            ? 'bg-blue-600/50 text-blue-300' 
            : 'bg-blue-200 text-blue-700'
          : isDarkMode 
            ? 'bg-blue-600/20 text-blue-300 hover:bg-blue-600/30' 
            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
      }`}
      disabled={isLoading && !isSpeaking}
      title={
        isLoading 
          ? `Generating audio: ${progress}%` 
          : isSpeaking 
            ? `Stop speaking (${selectedVoice.name})` 
            : `Speak text with ${selectedVoice.name}`
      }
    >
      <i className={`fas ${isLoading && !isSpeaking ? 'fa-spinner fa-spin' : (isSpeaking ? 'fa-stop-circle' : 'fa-volume-up')}`}></i>
      {isLoading && progress > 0 && !isSpeaking && <span className="ml-1 text-xs">{progress}%</span>}
    </button>
  );
};

export default TextToSpeech;